import { TICKS_PER_BAR, TICKS_PER_BEAT } from '../../shared/tempo';
import type { ClassDefinition, UpgradeDefinition, ZoneDefinition } from '../types';

export interface ShootingStarAttack {
  kind: 'shootingStar';
  damage: number;
  cooldownTicks: number;
  speed: number;
  radius: number;
  rangeTicks: number;
  spreadRadians: number;
  star: ZoneDefinition;
  constellation: { reach: number; width: number; damage: number; boost: number };
}

export interface MeteorsEffect {
  kind: 'meteors';
  range: number;
  distance: number;
  meteors: number;
  reach: number;
  fallTicks: number;
  damage: number;
  radius: number;
}

export const CELESTE: ClassDefinition = {
  id: 'celeste',
  name: 'Le Céleste',
  role: 'Laisse ses étoiles au sol et les relie en constellations qui piègent les bad vibes et renforcent les tirs de l’équipe.',
  color: '#7f9cff',
  maxHp: 110,
  speed: 7.2,
  radius: 14,
  pickupRadius: 120,
  attack: {
    kind: 'shootingStar',
    damage: 10,
    cooldownTicks: TICKS_PER_BEAT,
    speed: 16,
    radius: 6,
    rangeTicks: 20,
    spreadRadians: 0.2,
    star: {
      id: 'etoile',
      radius: 10,
      durationBars: 2,
      maxPerPlayer: 5,
      cadence: 'beat',
    },
    constellation: { reach: 120, width: 8, damage: 12, boost: 0.5 },
  },
  skill: {
    id: 'pluie-de-meteores',
    name: 'Pluie de météores',
    description:
      'Tu ouvres un portail là où tu vises, d’où tombent en rythme les météores d’une autre galaxie.',
    cooldownTicks: 4 * TICKS_PER_BAR,
    effect: {
      kind: 'meteors',
      range: 320,
      distance: 160,
      meteors: 4,
      reach: 120,
      fallTicks: 6,
      damage: 35,
      radius: 60,
    },
  },
  affinityWeaponIds: ['baton-du-diable'],
};

export const CELESTE_UPGRADES: readonly UpgradeDefinition[] = [
  {
    id: 'voie-lactee',
    name: 'Voie lactée',
    description: 'Tu gardes une étoile de plus au sol.',
    family: 'class',
    classId: 'celeste',
    modifiers: [{ stat: 'zoneCountAdd', add: 1 }],
    rarities: {
      rare: {
        description: 'Tu gardes deux étoiles de plus au sol.',
        modifiers: [{ stat: 'zoneCountAdd', add: 2 }],
      },
      legendary: {
        description: 'Tu gardes trois étoiles de plus au sol.',
        modifiers: [{ stat: 'zoneCountAdd', add: 3 }],
      },
    },
    maxStacks: 2,
  },
  {
    id: 'annees-lumiere',
    name: 'Années-lumière',
    description: 'Tes étoiles se relient de plus loin.',
    family: 'class',
    classId: 'celeste',
    modifiers: [{ stat: 'attackRadiusMul', mul: 1.2 }],
    rarities: {
      rare: {
        description: 'Tes étoiles se relient de nettement plus loin.',
        modifiers: [{ stat: 'attackRadiusMul', mul: 1.35 }],
      },
      legendary: {
        description: 'Tes étoiles se relient de moitié plus loin.',
        modifiers: [{ stat: 'attackRadiusMul', mul: 1.5 }],
      },
    },
    maxStacks: 2,
  },
  {
    id: 'perseides',
    name: 'Perséides',
    description: 'Ton portail lâche deux météores de plus, un par temps.',
    family: 'class',
    classId: 'celeste',
    modifiers: [{ stat: 'skillCountAdd', add: 2 }],
    rarities: {
      rare: {
        description: 'Ton portail lâche trois météores de plus, un par temps.',
        modifiers: [{ stat: 'skillCountAdd', add: 3 }],
      },
      legendary: {
        description: 'Ton portail lâche quatre météores de plus, un par temps.',
        modifiers: [{ stat: 'skillCountAdd', add: 4 }],
      },
    },
    maxStacks: 2,
  },
  {
    id: 'comete',
    name: 'Comète',
    description: 'Ta spéciale revient plus vite.',
    family: 'class',
    classId: 'celeste',
    modifiers: [{ stat: 'skillCooldownMul', mul: 0.64 }],
    rarities: {
      rare: {
        description: 'Ta spéciale revient bien plus vite.',
        modifiers: [{ stat: 'skillCooldownMul', mul: 0.36 }],
      },
      legendary: {
        description: 'Ta spéciale revient presque aussitôt.',
        modifiers: [{ stat: 'skillCooldownMul', mul: 0.16 }],
      },
    },
    maxStacks: 2,
  },
];
